import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Section from '../ui/Section';
import { experiences } from '../../data/experiences';
import { Briefcase, ChevronRight } from 'lucide-react';

const Experience: React.FC = () => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  return (
    <Section 
      id="experiences" 
      title="Esperienze Lavorative"
      subtitle="Il mio percorso professionale attraverso le aziende e i progetti più significativi."
    >
      <div className="relative">
        {/* Timeline line */}
        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700 transform -translate-x-1/2"></div>
        
        <div className="space-y-12">
          {experiences.map((experience, index) => {
            const isPresent = experience.period.toLowerCase().includes('presente');

            return (
              <motion.div
                key={experience.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className={`md:flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  
                  {/* Timeline dot */}
                  <div className={`hidden md:flex absolute left-1/2 top-0 transform -translate-x-1/2 w-10 h-10 rounded-full items-center justify-center z-10 ${
                    isPresent 
                      ? 'bg-primary-400 ring-4 ring-primary-200 dark:ring-primary-800 shadow-[0_0_15px_rgba(14,165,233,0.65)]' 
                      : 'bg-primary-600 dark:bg-primary-500'
                  }`}>
                    {isPresent && <span className="absolute inset-0 rounded-full bg-primary-400/40 animate-ping" />}
                    <Briefcase size={20} className="text-white relative z-10" />
                  </div>
                  
                  {/* Content */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 dark:border-gray-700">
                      
                      {/* Header */}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 gap-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                          {experience.company}
                          {isPresent && (
                            <span className="ml-2 text-xs font-semibold px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
                              Attuale
                            </span>
                          )}
                        </h3>

                        <span className="text-xs font-medium px-3 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 rounded-full w-fit">
                          {experience.period}
                        </span>
                      </div>
                      
                      {/* Role */}
                      <h4 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">
                        {experience.position}
                      </h4>
                      
                      {/* Description */}
                      <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                        {experience.description}
                      </p>
                      
                      {/* Achievements */}
                      <div>
                        <h5 className="text-sm font-semibold text-primary-700 dark:text-primary-300 mb-2 uppercase tracking-wide">
                          Impatto reale
                        </h5>

                        <ul className="space-y-1">
                          {(expanded[experience.id] ? experience.achievements : experience.achievements.slice(0, 6)).map((achievement, i) => (
                            <li key={i} className="flex items-start">
                              <ChevronRight size={16} className="text-primary-600 dark:text-primary-400 mt-1 mr-2 flex-shrink-0" />
                              <span className="text-gray-700 dark:text-gray-300 text-sm">
                                {achievement}
                              </span>
                            </li>
                          ))}
                        </ul>

                        {experience.achievements.length > 6 && (
                          <div
                            className="text-xs text-primary-600 dark:text-primary-400 mt-2 cursor-pointer hover:underline"
                            onClick={() => setExpanded(prev => ({ ...prev, [experience.id]: !prev[experience.id] }))}
                          >
                            {expanded[experience.id]
                              ? '- mostra meno'
                              : `+ altri ${experience.achievements.length - 6} risultati`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Mobile dot */}
                  <div className={`md:hidden absolute left-6 -top-6 z-10 w-10 h-10 rounded-full flex items-center justify-center ${
                    isPresent 
                      ? 'bg-primary-400 ring-4 ring-primary-200 dark:ring-primary-800 shadow-[0_0_15px_rgba(14,165,233,0.65)]' 
                      : 'bg-primary-600 dark:bg-primary-500'
                  }`}>
                    {isPresent && <span className="absolute inset-0 rounded-full bg-primary-400/40 animate-ping" />}
                    <Briefcase size={20} className="text-white relative z-10" />
                  </div>

                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};
export default Experience;